"use client";

import React from "react";
import { Zap, Maximize2, Truck, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const checkCards = [{
  step: "01",
  title: "Power Check",
  icon: Zap,
  description: "Mechanical bull setup requirements vary between models. Before ordering, confirm the power requirements for the exact machine, controller and blower rather than assuming every bull uses the same electrical supply."
}, {
  step: "02",
  title: "Space & Operating Clearance",
  icon: Maximize2,
  description: "Also measure the complete operating area. The mechanical base is only part of the space required. You may also need room for the inflatable safety area, operating clearance and access around the equipment."
}, {
  step: "03",
  title: "Access & Logistics",
  icon: Truck,
  description: "Finally, consider how the machine will reach the installation area. Packed dimensions, doors, gates, loading access and unloading requirements can all matter when receiving large equipment."
}];
export default function Powerspace() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="power-space-access" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground">
            Check Power, Space and Access Before Ordering
          </h2>
          <div className="mt-3 mx-auto h-0.5 w-16 bg-primary" />
        </div>

        {/* Industrial 3-Column Spec Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {checkCards.map((card, index) => {
          const Icon = card.icon;
          return <motion.article key={card.step} data-index={index} variants={cardVariants} className="group relative flex flex-col justify-between bg-card text-card-foreground border border-border p-6 sm:p-7 transition-[border-color,box-shadow] duration-200 hover:border-primary shadow-xs">
                {/* Card Top Metadata */}
                <div>
                  <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                    <div className="p-2 bg-muted text-primary border border-border">
                      <Icon className="size-4" />
                    </div>
                    <span className="font-mono text-xs text-muted-foreground/80 font-semibold tracking-widest">
                      {card.step}
                    </span>
                  </div>

                  {/* Card Title */}
                  <h3 className="font-serif text-xl sm:text-2xl font-bold tracking-tight uppercase text-foreground mb-3">
                    {card.title}
                  </h3>

                  {/* Body Paragraph */}
                  <p className="font-sans text-sm sm:text-base leading-relaxed text-muted-foreground">
                    {card.description}
                  </p>
                </div>
              </motion.article>;
        })}
        </motion.div>

        {/* Action Center */}
        <div className="mt-12 md:mt-16 text-center">
          <Button asChild size="lg" className="bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-8 py-6 rounded-none hover:bg-primary/90 transition-colors shadow-sm">
            <Link to="/SetupSafetyAndMaintenancePage" className="inline-flex items-center gap-2">
              <span>VIEW SETUP & SAFETY INFORMATION</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}